/**
 * Per-section profile completeness for the member edit experience.
 * Scores filled relevant fields as a weighted 0–100% percentage.
 */

export type ProfileSectionId =
  | 'personal'
  | 'activities'
  | 'work'
  | 'studies'
  | 'committees';

export type CompletenessStatus = 'incomplete' | 'partial' | 'complete' | 'notApplicable';

export interface SectionCompleteness {
  sectionId: ProfileSectionId;
  percentage: number;
  status: CompletenessStatus;
  /** False when the section does not apply (e.g. work when not working). */
  applicable: boolean;
  filledWeight: number;
  totalWeight: number;
}

export interface OverallCompleteness {
  percentage: number;
  status: CompletenessStatus;
  sections: Record<ProfileSectionId, SectionCompleteness>;
}

type WeightedCheck = { weight: number; filled: boolean };

function isNonEmpty(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value === 'boolean') return true;
  return false;
}

function either(...values: unknown[]): boolean {
  return values.some(isNonEmpty);
}

function both(...values: unknown[]): boolean {
  return values.length > 0 && values.every(isNonEmpty);
}

function scoreChecks(checks: WeightedCheck[]): Pick<
  SectionCompleteness,
  'percentage' | 'filledWeight' | 'totalWeight' | 'status'
> {
  const totalWeight = checks.reduce((sum, c) => sum + c.weight, 0);
  const filledWeight = checks.reduce((sum, c) => sum + (c.filled ? c.weight : 0), 0);
  const percentage =
    totalWeight === 0 ? 100 : Math.round((filledWeight / totalWeight) * 100);

  let status: CompletenessStatus;
  if (percentage >= 100) status = 'complete';
  else if (percentage <= 0) status = 'incomplete';
  else status = 'partial';

  return { percentage, filledWeight, totalWeight, status };
}

function naResult(sectionId: ProfileSectionId): SectionCompleteness {
  return {
    sectionId,
    percentage: 100,
    status: 'notApplicable',
    applicable: false,
    filledWeight: 0,
    totalWeight: 0,
  };
}

/** Loose form shape used by RegisterIndividual edit mode / MemberProfileSectionEditor. */
export interface MemberEditFormLike {
  title?: string;
  firstNameEn?: string;
  firstNameGr?: string;
  lastNameEn?: string;
  lastNameGr?: string;
  gender?: string | number;
  dateOfBirth?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  website?: string;
  contactPreference?: string | number;
  membershipType?: string | number;
  residentialAddressEn?: string;
  residentialAddressGr?: string;
  residentialCityEn?: string;
  residentialCityGr?: string;
  residentialPostalCode?: string;
  residentialCountryEn?: string;
  residentialCountryGr?: string;
  professionalActivities?: {
    working?: boolean;
    studying?: boolean;
    retired?: boolean;
  };
  professionalTitleEn?: string;
  professionalTitleGr?: string;
  workSector?: string | number;
  professionalInstitutionNameEn?: string;
  professionalInstitutionNameGr?: string;
  professionalAddressEn?: string;
  professionalAddressGr?: string;
  professionalCityEn?: string;
  professionalCityGr?: string;
  professionalPostalCode?: string;
  professionalEmail?: string;
  professionalPhone?: string;
  studies?: Array<{
    institution?: string;
    institutionEn?: string;
    institutionGr?: string;
    titleEn?: string;
    titleGr?: string;
  }>;
  mainCommittee?: string;
  additionalCommittees?: string[];
}

export function computePersonalCompleteness(form: MemberEditFormLike): SectionCompleteness {
  // Date of birth is optional enrichment — excluded from readiness scoring.
  const checks: WeightedCheck[] = [
    { weight: 2, filled: either(form.firstNameEn, form.firstNameGr) },
    { weight: 2, filled: both(form.lastNameEn, form.lastNameGr) },
    { weight: 2, filled: either(form.phone, form.mobile) },
    { weight: 2, filled: either(form.residentialAddressEn, form.residentialAddressGr) },
    { weight: 1, filled: either(form.residentialCityEn, form.residentialCityGr) },
    { weight: 1, filled: isNonEmpty(form.residentialPostalCode) },
    { weight: 1, filled: either(form.residentialCountryEn, form.residentialCountryGr) },
    { weight: 1, filled: isNonEmpty(form.gender) },
    { weight: 1, filled: isNonEmpty(form.title) },
    { weight: 1, filled: isNonEmpty(form.contactPreference) },
    { weight: 1, filled: isNonEmpty(form.email) },
  ];

  return {
    sectionId: 'personal',
    applicable: true,
    ...scoreChecks(checks),
  };
}

export function computeActivitiesCompleteness(form: MemberEditFormLike): SectionCompleteness {
  const activities = form.professionalActivities;
  const hasAny =
    !!activities?.working || !!activities?.studying || !!activities?.retired;

  return {
    sectionId: 'activities',
    applicable: true,
    percentage: hasAny ? 100 : 0,
    status: hasAny ? 'complete' : 'incomplete',
    filledWeight: hasAny ? 1 : 0,
    totalWeight: 1,
  };
}

export function computeWorkCompleteness(form: MemberEditFormLike): SectionCompleteness {
  if (!form.professionalActivities?.working) {
    return naResult('work');
  }

  const checks: WeightedCheck[] = [
    {
      weight: 3,
      filled: either(
        form.professionalInstitutionNameEn,
        form.professionalInstitutionNameGr
      ),
    },
    {
      weight: 2,
      filled: either(form.professionalTitleEn, form.professionalTitleGr),
    },
    { weight: 2, filled: isNonEmpty(form.workSector) },
    { weight: 1, filled: either(form.professionalEmail, form.professionalPhone) },
    {
      weight: 1,
      filled: either(form.professionalCityEn, form.professionalCityGr),
    },
    {
      weight: 1,
      filled: either(form.professionalAddressEn, form.professionalAddressGr),
    },
  ];

  return {
    sectionId: 'work',
    applicable: true,
    ...scoreChecks(checks),
  };
}

export function computeStudiesCompleteness(form: MemberEditFormLike): SectionCompleteness {
  if (!form.professionalActivities?.studying) {
    return naResult('studies');
  }

  const studies = form.studies ?? [];
  if (studies.length === 0) {
    return {
      sectionId: 'studies',
      applicable: true,
      percentage: 0,
      status: 'incomplete',
      filledWeight: 0,
      totalWeight: 1,
    };
  }

  const checks: WeightedCheck[] = studies.flatMap(study => [
    {
      weight: 2,
      filled: either(study.institutionEn, study.institutionGr, study.institution),
    },
    {
      weight: 1,
      filled: either(study.titleEn, study.titleGr),
    },
  ]);

  return {
    sectionId: 'studies',
    applicable: true,
    ...scoreChecks(checks),
  };
}

export function computeCommitteesCompleteness(form: MemberEditFormLike): SectionCompleteness {
  const hasMain = isNonEmpty(form.mainCommittee);

  // Main committee is required. Additional committees are optional and do not reduce score.
  return {
    sectionId: 'committees',
    applicable: true,
    percentage: hasMain ? 100 : 0,
    status: hasMain ? 'complete' : 'incomplete',
    filledWeight: hasMain ? 1 : 0,
    totalWeight: 1,
  };
}

export function computeSectionCompleteness(
  sectionId: ProfileSectionId,
  form: MemberEditFormLike
): SectionCompleteness {
  switch (sectionId) {
    case 'personal':
      return computePersonalCompleteness(form);
    case 'activities':
      return computeActivitiesCompleteness(form);
    case 'work':
      return computeWorkCompleteness(form);
    case 'studies':
      return computeStudiesCompleteness(form);
    case 'committees':
      return computeCommitteesCompleteness(form);
    default:
      return naResult(sectionId);
  }
}

export function computeAllSectionCompleteness(
  form: MemberEditFormLike
): Record<ProfileSectionId, SectionCompleteness> {
  return {
    personal: computePersonalCompleteness(form),
    activities: computeActivitiesCompleteness(form),
    work: computeWorkCompleteness(form),
    studies: computeStudiesCompleteness(form),
    committees: computeCommitteesCompleteness(form),
  };
}

/**
 * Aggregate % across applicable sections only (N/A sections excluded).
 */
export function computeOverallCompleteness(form: MemberEditFormLike): OverallCompleteness {
  const sections = computeAllSectionCompleteness(form);
  const applicable = Object.values(sections).filter(s => s.applicable);

  if (applicable.length === 0) {
    return { percentage: 100, status: 'complete', sections };
  }

  // Equal weight per applicable section so overall mirrors section progress rings.
  const percentage = Math.round(
    applicable.reduce((sum, s) => sum + s.percentage, 0) / applicable.length
  );

  let status: CompletenessStatus;
  if (percentage >= 100) status = 'complete';
  else if (percentage <= 0) status = 'incomplete';
  else status = 'partial';

  return { percentage, status, sections };
}

/** Admin edit form uses bilingual personal fields (matching registration) and different section IDs. */
export type AdminSectionId = 'personal' | 'professional';

export interface AdminEditFormLike {
  email?: string;
  title?: string;
  firstNameEn?: string;
  firstNameGr?: string;
  lastNameEn?: string;
  lastNameGr?: string;
  gender?: string | number;
  dateOfBirth?: string;
  residentialAddressEn?: string;
  residentialAddressGr?: string;
  residentialCityEn?: string;
  residentialCityGr?: string;
  residentialPostalCodeEn?: string;
  residentialPostalCodeGr?: string;
  residentialCountryEn?: string;
  residentialCountryGr?: string;
  phone?: string;
  mobile?: string;
  contactPreference?: string | number;
  professionalPath?: string | number;
  professionalTitleEn?: string;
  professionalTitleGr?: string;
  workSector?: string | number;
  professionalInstitutionNameEn?: string;
  professionalInstitutionNameGr?: string;
  professionalAddress?: string;
  professionalAddressEn?: string;
  professionalAddressGr?: string;
  professionalCity?: string;
  professionalCityEn?: string;
  professionalCityGr?: string;
  professionalPostalCode?: string;
  professionalEmail?: string;
  professionalPhone?: string;
  studies?: Array<{
    institution?: string;
    institutionEn?: string;
    institutionGr?: string;
    titleEn?: string;
    titleGr?: string;
  }>;
  membershipType?: string | number;
  registrationYear?: number;
  mainCommittee?: string;
  additionalCommittees?: string[];
}

export function computeAdminSectionCompleteness(
  sectionId: AdminSectionId,
  form: AdminEditFormLike
): SectionCompleteness {
  if (sectionId === 'personal') {
    const checks: WeightedCheck[] = [
      { weight: 2, filled: either(form.firstNameEn, form.firstNameGr) },
      { weight: 2, filled: both(form.lastNameEn, form.lastNameGr) },
      { weight: 2, filled: isNonEmpty(form.email) },
      { weight: 2, filled: isNonEmpty(form.dateOfBirth) },
      { weight: 2, filled: either(form.phone, form.mobile) },
      { weight: 2, filled: either(form.residentialAddressEn, form.residentialAddressGr) },
      { weight: 1, filled: either(form.residentialCityEn, form.residentialCityGr) },
      { weight: 1, filled: either(form.residentialPostalCodeEn, form.residentialPostalCodeGr) },
      { weight: 1, filled: either(form.residentialCountryEn, form.residentialCountryGr) },
      { weight: 1, filled: isNonEmpty(form.gender) },
      { weight: 1, filled: isNonEmpty(form.title) },
      { weight: 2, filled: isNonEmpty(form.mainCommittee) },
      { weight: 1, filled: isNonEmpty(form.membershipType) },
      { weight: 1, filled: isNonEmpty(form.registrationYear) },
    ];
    return { sectionId: 'personal', applicable: true, ...scoreChecks(checks) };
  }

  const hasPath = isNonEmpty(form.professionalPath);
  const studies = form.studies ?? [];
  const studyFilled =
    studies.length > 0 &&
    studies.some(s => either(s.institutionEn, s.institutionGr, s.institution));

  const checks: WeightedCheck[] = [
    { weight: 2, filled: hasPath },
    { weight: 1, filled: isNonEmpty(form.contactPreference) },
    {
      weight: 2,
      filled: either(
        form.professionalInstitutionNameEn,
        form.professionalInstitutionNameGr
      ),
    },
    {
      weight: 1,
      filled: either(form.professionalTitleEn, form.professionalTitleGr),
    },
    { weight: 1, filled: isNonEmpty(form.workSector) },
    { weight: 1, filled: either(form.professionalEmail, form.professionalPhone) },
    { weight: 1, filled: studyFilled },
  ];
  return { sectionId: 'work', applicable: true, ...scoreChecks(checks) };
}
